import React, { useEffect, useState } from "react"
import { Alert, Box, CircularProgress, Paper, Stack, Typography, useTheme } from "@mui/material"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  Tooltip,
  Legend
} from "chart.js"
import { Line } from "react-chartjs-2"
import type { PersistedMonthlyReport } from "@/features/profit-calculators/calculator-shared/types"
import { getMonthlyReportDetail } from "@/features/profit-calculators/calculator-shared/services/monthlyReportsService"

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, LineController, Tooltip, Legend)

interface Props {
  reports: PersistedMonthlyReport[]
  year: number
  platform: "amazon" | "meesho"
}

interface MonthlyPoint {
  monthKey: string
  sales: number | null
  profit: number
  loss: number
}

const currency = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2
  }).format(value)

function readSales(metadata: unknown): number | null {
  if (!metadata || typeof metadata !== "object" || !("salesIncGst" in metadata)) return null
  const sales = metadata.salesIncGst
  return typeof sales === "number" && Number.isFinite(sales) ? sales : null
}

function readMeeshoSales(orders: Awaited<ReturnType<typeof getMonthlyReportDetail>>["orders"]): number | null {
  if (!Array.isArray(orders)) return null
  const amounts = orders.map(order => (order.originalOrderRow ? order.originalOrderRow.totalSaleAmount : 0))
  if (amounts.some(amount => typeof amount !== "number" || !Number.isFinite(amount))) return null
  return amounts.reduce<number>((total, amount) => total + (amount ?? 0), 0)
}

const MonthlyPerformanceChart: React.FC<Props> = ({ reports, year, platform }) => {
  const platformName = platform === "amazon" ? "Amazon" : "Meesho"
  const theme = useTheme()
  const [result, setResult] = useState<{
    reports: PersistedMonthlyReport[]
    year: number
    platform: "amazon" | "meesho"
    points: MonthlyPoint[]
  } | null>(null)

  useEffect(() => {
    let cancelled = false
    const platformReports = reports
      .filter(report => report.platform === platform && report.monthKey.startsWith(`${year}-`))
      .sort((a, b) => a.monthKey.localeCompare(b.monthKey))

    Promise.all(
      platformReports.map(async report => {
        let sales: number | null = null
        try {
          const detail = await getMonthlyReportDetail(report.detailStorageRef)
          sales = platform === "amazon" ? readSales(detail.amazonMetadata) : readMeeshoSales(detail.orders)
        } catch {
          // Keep profit/loss visible when an archived report cannot be downloaded.
        }
        return {
          monthKey: report.monthKey,
          sales,
          profit: Math.max(report.netProfit, 0),
          loss: Math.max(-report.netProfit, 0)
        }
      })
    ).then(points => {
      if (!cancelled) setResult({ reports, year, platform, points })
    })
    return () => {
      cancelled = true
    }
  }, [reports, year, platform])

  const points =
    result?.reports === reports && result.year === year && result.platform === platform ? result.points : null
  const series = [
    {
      key: "sales" as const,
      label: "Sales",
      color: theme.palette.primary.main,
      dash: []
    },
    {
      key: "profit" as const,
      label: "Profit",
      color: theme.palette.success.main,
      dash: [8, 4]
    },
    {
      key: "loss" as const,
      label: "Loss",
      color: theme.palette.error.main,
      dash: [2, 4]
    }
  ]
  const months = Array.from({ length: 12 }, (_, month) =>
    new Date(Date.UTC(year, month)).toLocaleString("en", {
      month: "short",
      timeZone: "UTC"
    })
  )
  const chartData = {
    labels: months,
    datasets: series.map(item => ({
      label: item.label,
      data: months.map((_, month) => {
        const monthKey = `${year}-${String(month + 1).padStart(2, "0")}`
        return points?.find(point => point.monthKey === monthKey)?.[item.key] ?? null
      }),
      borderColor: item.color,
      backgroundColor: item.color,
      borderDash: item.dash,
      borderWidth: 2.5,
      pointRadius: item.key === "profit" ? 6 : 4,
      pointBackgroundColor: theme.palette.background.paper,
      pointBorderWidth: 2,
      spanGaps: false
    }))
  }

  return (
    <Paper
      variant="outlined"
      component="section"
      aria-label={`${platformName} sales, profit and loss`}
      sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3 }}>
      <Typography variant="h6" fontWeight={750}>
        Sales, profit &amp; loss
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
        Monthly performance for {year}, based on your uploaded reports.{" "}
        {platform === "amazon"
          ? "Sales include GST."
          : "Sales show the total sale amounts from monthly payment orders, before sale returns."}{" "}
        Profit and loss show the positive and negative portions of monthly net profit after costs; loss is shown as a
        positive amount.
      </Typography>
      {!points ? (
        <Stack alignItems="center" sx={{ py: 5 }}>
          <CircularProgress aria-label="Loading performance chart" />
        </Stack>
      ) : points.length === 0 ? (
        <Alert severity="info">
          Upload your {platformName} orders and {platform === "amazon" ? "transaction" : "payment"} files and generate a
          report to see your chart.
        </Alert>
      ) : (
        <>
          {points.some(point => point.sales === null) && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Sales are unavailable for some reports. Profit and loss are still shown.
            </Alert>
          )}
          <Box sx={{ overflowX: "auto" }}>
            <Box sx={{ position: "relative", height: 320, minWidth: 600 }}>
              <Line
                role="img"
                aria-label={`Monthly ${platformName} sales, profit and loss in INR for ${year}.`}
                data={chartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  animation: false,
                  interaction: { mode: "index", intersect: false },
                  plugins: {
                    legend: {
                      display: true,
                      position: "top",
                      align: "start",
                      labels: { color: theme.palette.text.primary }
                    },
                    tooltip: {
                      callbacks: {
                        label: context => `${context.dataset.label}: ${currency(context.parsed.y ?? 0)}`
                      }
                    }
                  },
                  scales: {
                    x: {
                      ticks: { color: theme.palette.text.secondary },
                      grid: { display: false }
                    },
                    y: {
                      beginAtZero: true,
                      title: {
                        display: true,
                        text: "Amount (₹)",
                        color: theme.palette.text.secondary
                      },
                      grid: { color: theme.palette.divider },
                      ticks: {
                        color: theme.palette.text.secondary,
                        callback: value =>
                          new Intl.NumberFormat("en-IN", {
                            notation: "compact",
                            maximumFractionDigits: 1
                          }).format(Number(value))
                      }
                    }
                  }
                }}
              />
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Months without uploaded reports are gaps. Hover over a point for its value. Click a legend to show or hide a
            series.
          </Typography>
        </>
      )}
    </Paper>
  )
}

export default MonthlyPerformanceChart
